import { useEffect, useRef } from 'react';

const MAX_VISIBLE_PAGES = 3;

const getVisiblePages = (currentPage: number, totalPages: number) => {
    const visiblePagesCount = Math.min(MAX_VISIBLE_PAGES, totalPages);
    const maxStartPage = totalPages - visiblePagesCount + 1;
    const startPage = Math.min(Math.max(currentPage - 1, 1), maxStartPage);
    const visiblePages: number[] = [];

    for (let page = startPage; page < startPage + visiblePagesCount; page += 1) {
        visiblePages.push(page);
    }

    return visiblePages;
};

export const usePagination = ({
    limit,
    onPageChange,
    onPageNormalize,
    page,
    total,
}: {
    limit: number;
    onPageChange: (targetPage: number) => void;
    onPageNormalize: (targetPage: number) => void;
    page: number;
    total: number;
}) => {
    const onPageNormalizeRef = useRef(onPageNormalize);
    onPageNormalizeRef.current = onPageNormalize;

    const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
    const normalizedPage = totalPages > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
    const currentPage = totalPages > 0 ? normalizedPage : 0;
    const visiblePages = getVisiblePages(currentPage, totalPages);
    const hasPreviousPage = currentPage > 1;
    const hasNextPage = currentPage < totalPages;

    useEffect(() => {
        if (page !== normalizedPage) {
            onPageNormalizeRef.current(normalizedPage);
        }
    }, [normalizedPage, page]);

    const changePage = (targetPage: number) => {
        if (targetPage !== currentPage && targetPage >= 1 && targetPage <= totalPages) {
            onPageChange(targetPage);
        }
    };

    return {
        changePage,
        currentPage,
        hasNextEllipsis: visiblePages[visiblePages.length - 1] < totalPages,
        hasNextPage,
        hasPreviousEllipsis: visiblePages[0] > 1,
        hasPreviousPage,
        totalPages,
        visiblePages,
    };
};
